import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { graphql } from "gatsby";
import { Button, Container } from "react-bootstrap";
import { ChevronRight, ChevronLeft } from "react-bootstrap-icons";
import classNames from "classnames";

import CarouselItem from "./CarouselItem";

import "../styles/components/DigitalCarousel.scss";

const DigitalCarousel = ({ title, childItems: items }) => {
    const containerRef = useRef(null);
    const [isScrollAtStart, setIsScrollAtStart] = useState(true);
    const [isScrollAtEnd, setIsScrollAtEnd] = useState(false);

    const scrollLeft = () => {
        if (!containerRef.current) return;

        containerRef.current.scrollLeft -= 200;
    };

    const scrollRight = () => {
        if (!containerRef.current) return;

        containerRef.current.scrollLeft += 200;
    };

    const checkScroll = () => {
        if (containerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } =
                containerRef.current;
            setIsScrollAtStart(scrollLeft === 0);
            setIsScrollAtEnd(scrollLeft + clientWidth >= scrollWidth);
        }
    };

    useEffect(() => {
        const scrollRef = containerRef.current;
        const { scrollWidth, clientWidth } = scrollRef;

        //Hide right arrow if cards fits in the container
        setIsScrollAtEnd(clientWidth === scrollWidth);

        const handleScroll = () => {
            if (scrollRef) {
                checkScroll();
            }
        };

        if (scrollRef) {
            scrollRef.addEventListener("scroll", handleScroll);
        }

        return () => {
            if (scrollRef) {
                scrollRef.removeEventListener("scroll", handleScroll);
            }
        };
    }, []);

    return (
        <Container className="digital-carousel">
            <h2>{title}</h2>
            <hr />

            <div className="position-relative">
                {!isScrollAtStart && (
                    <>
                        <Button
                            aria-label="scroll-left"
                            variant="link"
                            onClick={scrollLeft}
                            className="scroll-button left"
                        >
                            <ChevronLeft color="dark" size="38" />
                        </Button>
                        <div className="gradient-bg left" />
                    </>
                )}

                <div
                    ref={containerRef}
                    className={classNames("carousel-container p-2", {
                        "justify-content-around": items?.length === 3,
                    })}
                >
                    {items?.map(item => (
                        <CarouselItem key={item.id} {...item} />
                    ))}
                </div>

                {!isScrollAtEnd && (
                    <>
                        <Button
                            aria-label="scroll-right"
                            variant="link"
                            onClick={scrollRight}
                            className="scroll-button right"
                        >
                            <ChevronRight color="dark" size="38" />
                        </Button>
                        <div className="gradient-bg right" />
                    </>
                )}
            </div>
        </Container>
    );
};

export const query = graphql`
    fragment DynamicDigitalCarousel on ContentfulDigitalCarousel {
        sys {
            contentType {
                sys {
                    id
                }
            }
        }
        title
        childItems {
            id
            title
            media {
                gatsbyImageData(layout: CONSTRAINED)
                title
                description
                file {
                    contentType
                    url
                }
            }
            ctaLink
            ctaText
        }
    }
`;

DigitalCarousel.propTypes = {
    title: PropTypes.string.isRequired,
    childItems: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            title: PropTypes.string.isRequired,
            media: PropTypes.shape({
                title: PropTypes.string,
                description: PropTypes.string,
                mimeType: PropTypes.string,
                gatsbyImageData: PropTypes.object,
                file: PropTypes.shape({
                    contentType: PropTypes.string.isRequired,
                    url: PropTypes.string.isRequired,
                }),
            }),
            copy: PropTypes.shape({
                raw: PropTypes.string.isRequired,
            }),
            pdf: PropTypes.shape({
                title: PropTypes.string.isRequired,
                file: PropTypes.shape({
                    url: PropTypes.string.isRequired,
                    fileName: PropTypes.string.isRequired,
                    contentType: PropTypes.string.isRequired,
                }),
            }),
            ctaLink: PropTypes.string,
        })
    ).isRequired,
};

export default DigitalCarousel;
